import React, { useEffect, useState } from "react";
import { Route, Switch } from "react-router-dom";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faBell,
  faBinoculars,
  faHardHat,
  faDrawPolygon,
  faWeightHanging,
  faCarCrash,
  faUserSlash,
  faPeopleArrows,
  faVest,
} from "@fortawesome/free-solid-svg-icons";

import Tabs from "../navigation/tabs/Tabs";
import { Interval, TabItem } from "../navigation/tabs/TabsTypes";

import { AreaInStats } from "../stats/AreaInStats";
import { GlobalDashboardStats } from "../stats/GlobalDashboard";
import { HatWearingStats } from "../stats/HatWearingStats";
import { OverloadedStats } from "../stats/OverloadedStats";
import { CollisionStats } from "../stats/CollisionStats";
import { NoAreaStats } from "../stats/NoAreaStats";
import { VpInAreaStats } from "../stats/VpInAreaStats";
import { HvStats } from "../stats/HvStats";
import { PWOSHEStats } from "../stats/PWOSHEStats";

import classes from "./Home.module.css";
import { getClientSerials } from "../api/Auth";
import { subDays } from "date-fns";
import { findHost } from "../helpers/MappingSecurispot";
import i18n from "../i18n";

// Rule-based color palette
const ruleColors: Record<number, string> = {
  1: "#4caf50",
  2: "#1976d2",
  3: "#d32f2f",
  4: "#f57c00",
  5: "#9c27b0",
  6: "#607d8b",
  7: "#009688",
  8: "#fbc02d",
  9: "#8bc34a",
};

interface HomeProps {
  isMenuCollapsed: boolean;
}

// Tabs definition with rule icons & colors where applicable
const tabItems: TabItem[] = [
  {
    label: i18n.t("GlobalTab"),
    path: "/supervision/global",
    icon: faBinoculars as IconDefinition,
  },
  {
    label: i18n.t("AreaTab"),
    path: "/supervision/area",
    icon: faDrawPolygon as IconDefinition,
    color: ruleColors[1],
  },
  {
    label: i18n.t("HelmetTab"),
    path: "/supervision/helmet",
    icon: faHardHat as IconDefinition,
    color: ruleColors[2],
  },
  {
    label: i18n.t("OverloadedTab"),
    path: "/supervision/overloaded",
    icon: faWeightHanging as IconDefinition,
    color: ruleColors[4],
  },
  {
    label: i18n.t("collisionTab"),
    path: "/supervision/collision",
    icon: faCarCrash as IconDefinition,
    color: ruleColors[5],
  },
  {
    label: i18n.t("NoAreaTab"),
    path: "/supervision/noarea",
    icon: faUserSlash as IconDefinition,
    color: ruleColors[6],
  },
  {
    label: i18n.t("VpInAreaTab"),
    path: "/supervision/vpinarea",
    icon: faPeopleArrows as IconDefinition,
    color: ruleColors[7],
  },
  {
    label: i18n.t("HvTab"),
    path: "/supervision/hv",
    icon: faVest as IconDefinition,
    color: ruleColors[8],
  },
  {
    label: i18n.t("PWOSHETab"),
    path: "/supervision/pwoshe",
    icon: faVest as IconDefinition,
    color: ruleColors[9],
  },
];

const Home: React.FC<HomeProps> = ({ isMenuCollapsed }) => {
  const now = Date.now();
  const oneDayAgo = subDays(now, 1).setHours(0, 0, 0, 0);

  const [startDatePeriodTimestamp, setStartDatePeriodTimestamp] = useState<number>(oneDayAgo);
  const [endDatePeriodTimestamp, setEndDatePeriodTimestamp] = useState<number>(now);
  const [selectedTab, setSelectedTab] = useState<TabItem | undefined>(tabItems[0]);
  const [serials, setSerials] = useState<string[]>([]);
  const [selectedSerials, setSelectedSerials] = useState<string[]>([]);
  const [serialRequest, setSerialRequest] = useState<string>("");
  const [title, setTitle] = useState<string>(i18n.t("AllSecurispots"));

  // Update title / serial request when selection changes
  useEffect(() => {
    if (selectedSerials.length > 1) {
      setSerialRequest("");
      setTitle(i18n.t("AllSecurispots"));
    } else if (selectedSerials.length === 1) {
      setSerialRequest(selectedSerials[0]);
      setTitle(findHost(selectedSerials[0]));
    }
  }, [selectedSerials]);

  // Load serials on mount
  useEffect(() => {
    const userSerials = getClientSerials();
    if (userSerials) {
      setSerials(userSerials);
      setSelectedSerials(userSerials);
    }
  }, []);

  return (
    <>
      {/* ───────────────────────── Tabs */}
      <Tabs
        interval={Interval.DAY}
        items={tabItems}
        onIntervalChanged={() => console.log("interval changed")}
        onPeriodChange={(dates) => {
          setStartDatePeriodTimestamp(dates[0]);
          setEndDatePeriodTimestamp(dates[1]);
        }}
        onSelect={setSelectedTab}
        selectedTab={selectedTab}
        startDatePeriodTimestamp={startDatePeriodTimestamp}
        endDatePeriodTimestamp={endDatePeriodTimestamp}
        serials={serials}
        selectSerials={setSelectedSerials}
        selectedSerials={selectedSerials}
        isMenuCollapsed={isMenuCollapsed}
      />

      {/* ───────────────────────── Routed views */}
      <div className={classes.HomeContainer}>
        <Switch>
          <Route exact path="/supervision/global" render={() => <GlobalDashboardStats startDate={startDatePeriodTimestamp} endDate={endDatePeriodTimestamp} serial={serialRequest} title={title} />} />
          <Route exact path="/supervision/helmet" render={() => <HatWearingStats startDate={startDatePeriodTimestamp} endDate={endDatePeriodTimestamp} serial={serialRequest} title={title} />} />
          <Route exact path="/supervision/overloaded" render={() => <OverloadedStats startDate={startDatePeriodTimestamp} endDate={endDatePeriodTimestamp} serial={serialRequest} title={title} />} />
          <Route exact path="/supervision/area" render={() => <AreaInStats startDate={startDatePeriodTimestamp} endDate={endDatePeriodTimestamp} serial={serialRequest} title={title} />} />
          <Route exact path="/supervision/collision" render={() => <CollisionStats startDate={startDatePeriodTimestamp} endDate={endDatePeriodTimestamp} serial={serialRequest} title={title} />} />
          <Route exact path="/supervision/noarea" render={() => <NoAreaStats startDate={startDatePeriodTimestamp} endDate={endDatePeriodTimestamp} serial={serialRequest} title={title} />} />
          <Route exact path="/supervision/vpinarea" render={() => <VpInAreaStats startDate={startDatePeriodTimestamp} endDate={endDatePeriodTimestamp} serial={serialRequest} title={title} />} />
          <Route exact path="/supervision/hv" render={() => <HvStats startDate={startDatePeriodTimestamp} endDate={endDatePeriodTimestamp} serial={serialRequest} title={title} />} />
          <Route exact path="/supervision/pwoshe" render={() => <PWOSHEStats startDate={startDatePeriodTimestamp} endDate={endDatePeriodTimestamp} serial={serialRequest} title={title} />} />
        </Switch>
      </div>
    </>
  );
};

export default Home;
